<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { MAX_TAG_NAME_LENGTH } from '@/todos/hashtags'
import { highlightParts, type HashtagSuggestion } from '@/todos/tags'

// The list of tags offered while a hashtag is typed, with the matching letters in bold, and the warning about a tag over
// the length limit. It sits above the field it is placed in (below or `inside`, at the bottom of the field, with `placement`;
// those need `position: relative`). `inside` is for a field at the top of a scrolling sheet, which would clip a list above it.
withDefaults(
  defineProps<{
    matches: HashtagSuggestion[]
    active: number
    open: boolean
    tooLong?: boolean
    placement?: 'above' | 'below' | 'inside'
    listId?: string
    optionId?: (index: number) => string
  }>(),
  { tooLong: false, placement: 'above', listId: undefined, optionId: undefined },
)
const emit = defineEmits<{ pick: [name: string] }>()
const { t } = useI18n()
</script>

<template>
  <ul v-if="open" :id="listId" class="suggestions" :class="placement" role="listbox">
    <!-- pointerdown.prevent keeps the field focused, otherwise its blur would close the list first -->
    <li
      v-for="(match, index) in matches"
      :id="optionId?.(index)"
      :key="match.name"
      role="option"
      :aria-selected="index === active"
      :class="{ active: index === active }"
      @pointerdown.prevent="emit('pick', match.name)"
    >
      #<template v-for="(part, i) in highlightParts(match.name, match.ranges)" :key="i"
        ><b v-if="part.hit">{{ part.text }}</b
        ><template v-else>{{ part.text }}</template></template
      >
    </li>
  </ul>
  <p v-if="tooLong" class="warning" role="alert">
    {{ t('todos.tagTooLong', { max: MAX_TAG_NAME_LENGTH }) }}
  </p>
</template>

<style scoped>
.suggestions {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 10;
  margin: 0;
  padding: 0.2rem 0;
  list-style: none;
  border-radius: 0.5rem;
  background: var(--tg-secondary-bg-color, var(--tg-bg-color));
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.25);
}

.suggestions.above {
  bottom: 100%;
  margin-bottom: 0.2rem;
}

.suggestions.below {
  top: 100%;
  margin-top: 0.2rem;
}

.suggestions.inside {
  left: 0.2rem;
  right: 0.2rem;
  bottom: 0.2rem;
}

.suggestions li {
  padding: 0.4rem 0.7rem;
  cursor: pointer;
}

.suggestions b {
  font-weight: 700;
}

.suggestions li.active {
  background: var(--tg-button-color);
  color: var(--tg-button-text-color);
}

.warning {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: var(--tg-destructive-text-color);
}
</style>
